import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, message, Row, Col } from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  MailOutlined, 
  PhoneOutlined,
  ShopOutlined,
  BankOutlined,
  IdcardOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { stationOwnerAPI } from '../services/api';

const { Title, Text, Link } = Typography;

const Register: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      await stationOwnerAPI.register({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        phone: values.phone,
        companyName: values.companyName,
        taxNumber: values.taxNumber,
        address: values.address
      });

      message.success('Kayıt başarılı! Hesabınız admin onayından sonra aktif olacaktır.');
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.errors?.[0]?.message || 'Kayıt başarısız';
      message.error(errorMessage);
      console.error('Kayıt hatası:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px',
      overflow: 'auto'
    }}>
      <Card 
        style={{ 
          width: '100%', 
          maxWidth: 800,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          borderRadius: '12px',
          margin: '20px auto',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
        bodyStyle={{
          padding: '24px'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <ShopOutlined style={{ fontSize: '48px', color: '#FF6B35' }} />
          <Title level={2} style={{ marginTop: '16px', marginBottom: '8px' }}>
            YakıtGo İstasyon Sahibi Kaydı
          </Title>
          <Text type="secondary">
            İstasyonunuzu kaydedin, kampanyalar oluşturun
          </Text>
        </div>

        <Form
          form={form}
          name="register"
          onFinish={onFinish}
          layout="vertical"
          size="large"
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="firstName"
                label="Ad"
                rules={[{ required: true, message: 'Adınızı girin' }]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="Adınız"
                  style={{ color: '#333333' }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="lastName"
                label="Soyad"
                rules={[{ required: true, message: 'Soyadınızı girin' }]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="Soyadınız"
                  style={{ color: '#333333' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="E-posta"
                rules={[
                  { required: true, message: 'E-posta adresinizi girin' },
                  { type: 'email', message: 'Geçerli bir e-posta adresi girin' }
                ]}
              >
                <Input 
                  prefix={<MailOutlined />} 
                  placeholder="ornek@email.com"
                  style={{ color: '#333333' }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="phone"
                label="Telefon"
                rules={[{ required: true, message: 'Telefon numaranızı girin' }]}
              >
                <Input 
                  prefix={<PhoneOutlined />} 
                  placeholder="05xx xxx xx xx"
                  style={{ color: '#333333' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="password"
                label="Şifre"
                rules={[
                  { required: true, message: 'Şifrenizi girin' },
                  { min: 8, message: 'Şifre en az 8 karakter olmalı' },
                  {
                    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                    message: 'Şifre en az bir büyük harf, bir küçük harf ve bir rakam içermelidir'
                  }
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="Şifreniz"
                  style={{ color: '#333333' }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="confirmPassword"
                label="Şifre Tekrar"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Şifrenizi tekrar girin' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Şifreler eşleşmiyor'));
                    },
                  }),
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="Şifrenizi tekrar girin"
                  style={{ color: '#333333' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="companyName"
            label="Şirket Adı"
            rules={[{ required: true, message: 'Şirket adını girin' }]}
          >
            <Input 
              prefix={<BankOutlined />} 
              placeholder="ABC Petrol A.Ş."
              style={{ color: '#333333' }}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="taxNumber"
                label="Vergi Numarası"
                rules={[{ required: true, message: 'Vergi numarasını girin' }]}
              >
                <Input 
                  prefix={<IdcardOutlined />} 
                  placeholder="1234567890"
                  style={{ color: '#333333' }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="address"
                label="Adres"
                rules={[{ required: true, message: 'Adresinizi girin' }]}
              >
                <Input 
                  prefix={<ShopOutlined />} 
                  placeholder="Şirket adresi"
                  style={{ color: '#333333' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              block
              style={{
                height: '48px',
                fontSize: '16px',
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                marginTop: '8px'
              }}
            >
              Kayıt Ol
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Text>
              Zaten hesabınız var mı?{' '}
              <Link onClick={() => navigate('/login')} style={{ fontWeight: 'bold' }}>
                Giriş Yap
              </Link>
            </Text>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default Register;

